import React, { useEffect, useState } from 'react';

import { Paper, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';

import { IAdminClubActions } from '../../../store/admin/clubs/actions';
import { IAdminClub, IAdminClubsStore, IClubTransaction } from '../../../store/admin/clubs/store';

import { withContext } from '../../../contrib/utils';
import { Subheader } from '../Common/Subheader/Subheader';
import { ExpandedTransactionRow } from './ExpandedTransactionRow';
import { TransactionRow } from './TransactionRow';
import { TransactionsFilters } from './TransactionsFilters';
import { TransactionsHeader } from './TransactionsHeader';
import { filterTransactions } from './Utils/filterTransactions';
import { getFormattedTransactionData } from './Utils/getFormattedTransactionData';

export interface ICSVData {
  id: number;
  eventName: string;
  payeer: string;
  type: string;
  paymentMethod: string;
  amount: string;
  date: string;
}

export interface IFilterOptions {
  eventNames: Set<string>;
  payeers: Set<string>;
  types: Set<string>;
}

interface LedgerProps {
  actions: {
    adminClubs: IAdminClubActions;
  };
  adminClubs: IAdminClubsStore;
  clubId: number;
}

const LedgerComponent = (props: LedgerProps) => {
  const {
    actions: {
      adminClubs: { getTransactions, submitRefund }
    },
    adminClubs: { all: allClubs, transactions: allTransactions },
    clubId
  } = props;

  const club: IAdminClub = allClubs[clubId];

  const [collapsedRow, setCollapsedRow] = useState<number>(-1);

  const [openFiltersSection, setOpenFiltersSection] = useState<boolean>(false);

  const initialFilters: IFilterOptions = {
    eventNames: new Set<string>(),
    payeers: new Set<string>(),
    types: new Set<string>()
  };

  const [filterOptions, setFilterOptions] = useState(initialFilters);

  const [selectedEventNameFilterOption, setSelectedEventNameFilterOption] = useState('');
  const [selectedPayeerFilterOption, setSelectedPayeerFilterOption] = useState('');
  const [selectedTypeFilterOption, setSelectedTypeFilterOption] = useState('');
  const [selectedDateStartFilterOption, setSelectedDateStartFilterOption] = useState('');
  const [selectedDateEndFilterOption, setSelectedDateEndFilterOption] = useState('');

  const transactions = allTransactions[clubId];

  const clearFilters = () => {
    setSelectedEventNameFilterOption('');
    setSelectedPayeerFilterOption('');
    setSelectedTypeFilterOption('');
    setSelectedDateStartFilterOption('');
    setSelectedDateEndFilterOption('');
  };

  useEffect(() => {
    if (openFiltersSection && !!transactions && Object.keys(transactions).length > 0) {
      Object.values(transactions).map((transaction: IClubTransaction) => {
        const { eventName, payeer, type } = getFormattedTransactionData(transaction);

        setFilterOptions({
          ...filterOptions,
          eventNames: filterOptions.eventNames.add(eventName),
          payeers: filterOptions.payeers.add(payeer),
          types: filterOptions.types.add(type)
        });
      });
    }
  }, [transactions, openFiltersSection]);

  useEffect(() => {
    getTransactions(clubId);
  }, [transactions]);

  const [filteredTransactions, setFilteredTransactions] = useState<IClubTransaction[]>([]);

  useEffect(() => {
    if (transactions) {
      setFilteredTransactions(
        Object.values(transactions).filter((transaction: IClubTransaction) =>
          filterTransactions(
            selectedEventNameFilterOption,
            selectedPayeerFilterOption,
            selectedTypeFilterOption,
            selectedDateStartFilterOption,
            selectedDateEndFilterOption,
            transaction
          )
        )
      );
    }
  }, [
    selectedDateEndFilterOption,
    selectedDateStartFilterOption,
    selectedDateStartFilterOption,
    selectedEventNameFilterOption,
    selectedPayeerFilterOption,
    selectedTypeFilterOption,
    transactions
  ]);

  const [CSVData, setCSVData] = useState<ICSVData[]>([]);

  useEffect(() => {
    if (transactions) {
      setCSVData(
        filteredTransactions.map(
          (transaction: IClubTransaction): ICSVData => {
            const { amount, id } = transaction;

            const {
              eventName,
              formattedDate,
              payeer,
              paymentMethod,
              type
            } = getFormattedTransactionData(transaction);

            return { id, eventName, payeer, type, paymentMethod, amount, date: formattedDate };
          }
        )
      );
    }
  }, [transactions, openFiltersSection, filterOptions, filteredTransactions]);

  return (
    <div className="admin-layout ledger">
      <div>
        {club && <Subheader clubId={club.id} profileImage={club.profileImage} title="Finances" />}

        <div className="admin-wrapper">
          <Paper elevation={1} className="paper">
            {!!transactions && Object.keys(transactions).length > 0 ? (
              <>
                <TransactionsHeader
                  CSVData={CSVData}
                  clubId={clubId}
                  openFiltersSection={openFiltersSection}
                  setOpenFiltersSection={setOpenFiltersSection}
                />

                {openFiltersSection && (
                  <TransactionsFilters
                    {...{
                      clearFilters,
                      filterOptions,
                      selectedDateEndFilterOption,
                      selectedDateStartFilterOption,
                      selectedEventNameFilterOption,
                      selectedPayeerFilterOption,
                      selectedTypeFilterOption,
                      setSelectedDateEndFilterOption,
                      setSelectedDateStartFilterOption,
                      setSelectedEventNameFilterOption,
                      setSelectedPayeerFilterOption,
                      setSelectedTypeFilterOption
                    }}
                  />
                )}

                <Table>
                  <colgroup>
                    {[0, 1, 2, 3, 4, 6, 7].map(index => (
                      <col key={index} />
                    ))}
                  </colgroup>

                  <TableHead>
                    <TableRow>
                      <TableCell align="center">DATE</TableCell>
                      <TableCell align="center">ITEM OR EVENT NAME</TableCell>
                      <TableCell align="center">PAYER / PAYEE</TableCell>
                      <TableCell align="center">FEE TYPE</TableCell>
                      <TableCell align="center">AMOUNT</TableCell>
                      <TableCell align="center">PAYMENT METHOD</TableCell>
                      <TableCell align="center" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTransactions.map((transaction: IClubTransaction, index: number) => [
                      <TransactionRow
                        key={`tableRow-${index}`}
                        transaction={transaction}
                        index={index}
                        collapsedRow={collapsedRow}
                        setCollapsedRow={setCollapsedRow}
                      />,
                      <ExpandedTransactionRow
                        clubId={clubId}
                        collapsed={index === collapsedRow}
                        key={`hiddenTableRow-${index}`}
                        submitRefund={submitRefund}
                        transaction={transaction}
                      />
                    ])}
                  </TableBody>
                </Table>
              </>
            ) : (
              <div className="noTransactions">
                <p>Your club has not accepted any payments through WAGL.</p>
                <p>
                  Once payments are made in WAGL, this is the place to review transactions, provide
                  refunds, or download the spreadsheets for bookkeeping.
                </p>
              </div>
            )}
          </Paper>
        </div>
      </div>
    </div>
  );
};

const Ledger = withContext(LedgerComponent, 'adminClubs');

export { Ledger, LedgerComponent };
